import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import VideocamIcon from "@mui/icons-material/Videocam";
import { store } from "../../utils/store";
import { clickToCall } from "../Team/components/ShowAll/ShowAllService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import { formatPhoneKeepingCountryCode } from "../../Shared/Phone/PhoneWithCountryCode";

type Props = {
  booking: any;
  onClose: () => void;
};

const statusStyles: Record<string, React.CSSProperties> = {
  confirmed: { backgroundColor: "#E6F4EA", color: "#1E7F43" },
  cancelled: { backgroundColor: "#FDECEA", color: "#D32F2F" },
  rescheduled: { backgroundColor: "#FFF4E5", color: "#ED6C02" },
  completed: { backgroundColor: "#E3F2FD", color: "#1976D2" },
};

const BookingDetailTray: React.FC<Props> = ({ booking, onClose }) => {
  const [calling, setCalling] = useState(false);
  const appointmentType = String(booking?.appointmentType || "").toLowerCase();
  const isVirtual = appointmentType === "virtual";
  const isTelephonic = appointmentType === "telephonic";
  const meetLink = String(booking?.meetLink || "").trim();
  const phone = booking?.userData?.phone || "";
  const phoneMasked =
    booking?.userData?.phoneMasked === true || String(phone).includes("*");
  const phoneDisplay = formatPhoneKeepingCountryCode(phone);
  const status = String(booking?.status || "").toLowerCase();

  const handleJoin = () => {
    if (!meetLink) {
      openErrorNotification("Google Meet link is not available yet");
      return;
    }
    window.open(meetLink, "_blank", "noopener,noreferrer");
  };

  const handleCall = async () => {
    if (!phone && !booking?._id) {
      openErrorNotification("Phone number not available");
      return;
    }
    try {
      setCalling(true);
      const payload: any = {
        uuid: store.getState().auth.uuid,
        bookingId: booking?._id,
      };
      // When unmasked, pass the number; when masked, backend resolves via bookingId
      if (!phoneMasked && phone) {
        payload.leadPhone = phone;
      } else {
        payload.leadPhone = phone || "";
      }
      const response = await clickToCall(payload);
      if (response?.success === 0 || response?.success === false) {
        openErrorNotification(response?.message || "Unable to place call");
        return;
      }
      openSuccessNotification(response?.message || "Call initiated");
    } catch (e: any) {
      openErrorNotification(e?.message || "Unable to place call");
    } finally {
      setCalling(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2 border-b border-[#EEF0F5] px-4 py-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]">
            Booking details
          </p>
          <h3 className="mt-0.5 truncate text-lg font-bold text-[#111827]">
            {booking?.userData?.name || "Guest"}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] transition hover:bg-[#F3F4F6]"
          aria-label="Close"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4 [scrollbar-width:thin]">
        {isVirtual ? (
          <button
            type="button"
            onClick={handleJoin}
            disabled={!meetLink}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#A02448] px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#841C39] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <VideocamIcon sx={{ fontSize: 20 }} />
            {meetLink ? "Join Google Meet" : "Meet link unavailable"}
          </button>
        ) : null}

        {isTelephonic ? (
          <button
            type="button"
            onClick={handleCall}
            disabled={calling || (!phone && !booking?._id)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#28a745] px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#218838] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PhoneIcon sx={{ fontSize: 20 }} />
            {calling ? "Calling…" : "Call (IVR)"}
          </button>
        ) : null}

        <div className="space-y-3 rounded-xl border border-[#EEF0F5] bg-[#F8FAFC] p-3 text-sm">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Phone
            </p>
            <p className="mt-0.5 font-mono font-medium tracking-wide text-[#111827]">
              {phoneDisplay}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Email
            </p>
            <p className="mt-0.5 break-all font-medium text-[#111827]">
              {booking?.userData?.email || "—"}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Type
            </p>
            <p className="mt-0.5 font-medium capitalize text-[#111827]">
              {appointmentType || "—"}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Status
            </p>
            <span
              className="mt-1 inline-block rounded-xl px-2.5 py-1 text-xs font-semibold capitalize"
              style={statusStyles[status]}
            >
              {booking?.status || "—"}
            </span>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Date & slot
            </p>
            <p className="mt-0.5 font-medium text-[#111827]">
              {booking?.selectedDate
                ? new Date(booking.selectedDate).toLocaleDateString()
                : "—"}
            </p>
            <p className="text-[#6B7280]">{booking?.selectedSlot || "—"}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Host
            </p>
            <p className="mt-0.5 font-medium capitalize text-[#111827]">
              {booking?.meetingId?.host?.name || "—"}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
              Project
            </p>
            <p className="mt-0.5 font-medium capitalize text-[#111827]">
              {booking?.projectDetailId?.projectId?.project_name || "—"}
            </p>
            <p className="text-[#6B7280]">
              {[
                booking?.projectDetailId?.location,
                booking?.projectDetailId?.propertyPurpose,
              ]
                .filter(Boolean)
                .join(" · ") || "—"}
            </p>
          </div>
          {isVirtual && meetLink ? (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
                Meet link
              </p>
              <a
                href={meetLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-0.5 break-all text-sm font-medium text-[#A02448] hover:underline"
              >
                {meetLink}
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default BookingDetailTray;
